"use client"
import IconArrowRight from "@/app/(site)/_components/Atoms/Icons/IconArrowRight"
import IconClose from "@/app/(site)/_components/Atoms/Icons/IconClose"
import Text from "@/app/(site)/_components/Atoms/Text"
import { MenuItemTypes } from "@/app/(site)/_mockdata/menuItems"
import useBreakpoint from "@/app/(site)/_utils/_hooks/useBreakpoint"

import { getLangCode } from "@/app/(site)/_utils/misc"
import useEcommerceStore from "@/app/(site)/contexts/ecommerce/store"
import { t } from "@/translations"
import { usePathname } from "next/navigation"
import React, { useState } from "react"
import DynamicLink from "../../../Molecules/Link"

type Item = {
    title: string
    menuItems?: MenuItemTypes[]
    items?: string[]
}

type MobileNavProps = {
    menuItems: MenuItemTypes[]
}

const MobileNav = ({ menuItems }: MobileNavProps) => {
    const showMobileMenu = useEcommerceStore(state => state.showMobileMenu)
    const setShowMobileMenu = useEcommerceStore(state => state.setShowMobileMenu)

    const [path, setPath] = useState<Item[]>([])
    const ismobile = useBreakpoint(968)

    const params = usePathname()
    const locale = params.split("/")[1]
    const langCode = getLangCode(locale) ?? "en"

    React.useEffect(() => {
        if (ismobile) {
            setShowMobileMenu(false)
        }
    }, [ismobile, setShowMobileMenu])

    const handleToggleMenu = () => {
        setShowMobileMenu(!showMobileMenu)
        if (showMobileMenu) {
            setPath([]) // Reset the menu when closing
        }
    }

    const handleItemClick = (item: Item) => {
        setPath([...path, item])
    }

    const handleLinkClick = () => {
        setShowMobileMenu(false)
    }

    const handleBack = () => {
        setPath(path.slice(0, -1))
    }

    const renderMenuItems = (items: MenuItemTypes[], level: number = 1) => {
        return (
            <nav
                role="navigation"
                aria-label={
                    level === 1 ? t(langCode, "Main menu") : t(langCode, "Submenu")
                }
            >
                {/* Render parent section if available */}
                {items?.[0]?.parent && (
                    <div className="mb-16 flex items-center gap-8">
                        <div className="h-16 w-16 bg-main-black" />
                        {items[0].parent.isNavigationContainer ? (
                            <Text size="medium" weight="regular">
                                {items[0].parent.title}
                            </Text>
                        ) : (
                            <DynamicLink
                                href={items[0].parent.uri}
                                onClick={handleLinkClick}
                                prefetch={false}
                            >
                                <Text size="medium" weight="regular">
                                    {items[0].parent.title}
                                </Text>
                            </DynamicLink>
                        )}
                    </div>
                )}

                {/* Main menu rendering */}
                <ol
                    role="menu"
                    className={` ${level > 1 ? "border-l border-l-black" : ""}`}
                >
                    {items.map((item, index) => {
                        if (item.hidden) return null
                        const hasNonHiddenChild = item.menuItems?.some(
                            child => !child.hidden
                        )
                        const isExpanded = hasNonHiddenChild ? "true" : "false"
                        return (
                            <li
                                key={index}
                                role="menuitem"
                                className={`${item.menuItems?.length ? "has-dropdown" : ""}`}
                            >
                                {level === 1 && (
                                    <div className="border-t border-black pb-32 pt-17">
                                        {/* Render Level 1 items */}
                                        {item.children ? (
                                            <div
                                                role="button"
                                                className="flex items-center gap-8 py-4"
                                                onClick={() => handleItemClick(item)}
                                                aria-label={item.title}
                                                aria-controls={`submenu-${index}`}
                                            >
                                                <div className="h-16 w-16 bg-main-black" />
                                                <Text size="medium" weight="regular">
                                                    {item.title}
                                                </Text>
                                                <span
                                                    className="ml-auto"
                                                    aria-hidden="true"
                                                >
                                                    <IconArrowRight className="h-20 w-20" />
                                                </span>
                                            </div>
                                        ) : (
                                            <DynamicLink
                                                href={item.uri}
                                                onClick={() => {
                                                    handleLinkClick()
                                                    setPath([])
                                                }}
                                                className="flex items-center justify-between"
                                                prefetch={false}
                                            >
                                                <div className="flex items-center gap-8 py-4">
                                                    <div className="h-16 w-16 bg-main-black" />
                                                    <Text size="medium" weight="regular">
                                                        {item.title}
                                                    </Text>
                                                </div>
                                            </DynamicLink>
                                        )}
                                    </div>
                                )}

                                {/* Render Level 2+ items */}
                                {level > 1 && (
                                    <div
                                        id={`submenu-${index}`}
                                        role="menu"
                                        aria-hidden={!isExpanded}
                                        className="pl-10"
                                    >
                                        {hasNonHiddenChild ? (
                                            <div
                                                role="button"
                                                aria-expanded={isExpanded}
                                                aria-controls={`submenu-${index}`}
                                                aria-label={item.title}
                                                className="flex cursor-pointer items-center py-4"
                                                onClick={() => handleItemClick(item)}
                                            >
                                                <Text size="medium" weight="light">
                                                    {item.title}
                                                </Text>
                                            </div>
                                        ) : (
                                            <DynamicLink
                                                role="menuitem"
                                                href={item.uri}
                                                onClick={handleLinkClick}
                                                prefetch={false}
                                            >
                                                <Text
                                                    size="medium"
                                                    weight="light"
                                                    className="py-4"
                                                >
                                                    {item.title}
                                                </Text>
                                            </DynamicLink>
                                        )}
                                    </div>
                                )}
                            </li>
                        )
                    })}

                    {/* Optional: "View all" section */}
                    {items.map((item, index) => {
                        if (
                            level !== 1 &&
                            index === 1 &&
                            !item.parent?.isNavigationContainer
                        ) {
                            return (
                                <DynamicLink
                                    key={index}
                                    className="mb-16 flex items-center justify-between gap-16 pl-10"
                                    href={item.parent?.uri || "/"}
                                    onClick={handleLinkClick}
                                    prefetch={false}
                                >
                                    <Text size="medium" weight="regular" className="py-4">
                                        {t(langCode, "View all")}
                                    </Text>
                                </DynamicLink>
                            )
                        }
                        return <></>
                    })}
                </ol>
            </nav>
        )
    }

    const renderPath = () => {
        const pathItems = path.map(p => p.title)
        const fullPath = [t(langCode, "Menu"), ...pathItems]

        return fullPath.map((item, index) => {
            const isLast = index === fullPath.length - 1
            const itemStyle = isLast ? "text-main-black" : "text-main-dark-grey"

            return (
                <span key={index} className={itemStyle}>
                    {item}
                    {index < fullPath.length - 1 && " / "}
                </span>
            )
        })
    }

    return (
        <>
            {showMobileMenu && (
                <div
                    className={`fixed bottom-0 left-0 right-0 top-0 z-50 bg-white ${
                        showMobileMenu ? "fixed" : "hidden"
                    }`}
                >
                    <div className="px-16 py-24 md:p-24 md:px-24">
                        <div
                            className="mb-30 flex justify-between"
                            onClick={path.length > 0 ? handleBack : undefined}
                        >
                            <div>
                                <span className="nav-back"></span>
                                <span className="nav-title cursor-pointer">
                                    <Text size="medium" weight="regular">
                                        {renderPath()}
                                    </Text>
                                </span>
                                <span className="nav-close"></span>
                            </div>
                            <div onClick={handleToggleMenu} className="cursor-pointer">
                                <IconClose />
                            </div>
                        </div>
                        {renderMenuItems(
                            path.length === 0
                                ? menuItems
                                : path[path.length - 1].menuItems || [],
                            path.length + 1
                        )}
                    </div>
                </div>
            )}
        </>
    )
}

export default MobileNav
